import { useEffect, useRef, useState } from 'react'
import { DoorOpen, Ear, Lock, MessageSquare, Moon, PhoneForwarded, Play } from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { apiRead, usePermissions, useToast } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { runCommand } from '../access/lib.js'
import {
  appelRecent, cheminAudio, delai, duree, heureParis, libelleAction, libelleCibles, libelleDecroche, libelleEquipe, libelleType, messageAudio,
  messageErreur, quandParis, type FicheAppel, type Tour,
} from './interphone-model.js'

/**
 * SÉCURITÉ › INTERPHONE IA › FICHE D'APPEL (`/securite/interphone-ia/appels/:id`).
 *
 * Le résumé, puis la conversation échange par échange — ce que Sacha a
 * entendu, ce qu'elle a répondu, ses délais — avec la voix du visiteur à
 * réécouter. L'audio est relayé par Gestion (le jeton du service ne quitte
 * jamais le serveur) ; il est chargé à la demande, et un échec dit le VRAI
 * motif (effacé après 30 jours, service muet, droit manquant).
 *
 * « Ouvrir la porte STAFF » réutilise la commande Access existante
 * (ACCESS_DOOR_UNLOCK), visible seulement avec `access.doors.unlock`. Les
 * capacités Access (Mode sûr, politique d'action) sont lues AVANT : un bouton
 * bloqué le dit, avec sa raison, au lieu d'échouer après la confirmation.
 * Un appel de plus de 15 min n'a plus de bouton principal : la personne est
 * sans doute repartie ; « Ouvrir maintenant » reste possible, daté.
 */

type Lecture = { etat: 'chargement' } | { etat: 'ok'; data: FicheAppel } | { etat: 'erreur'; message: string }

interface CapaciteAccess { action: string; status: string; reason?: string | null }

/** La raison de blocage d'ACCESS_DOOR_UNLOCK d'après le serveur ; null = possible ou inconnu. */
function useBlocageOuverture(actif: boolean): { charge: boolean; raison: string | null } {
  const [etat, setEtat] = useState<{ charge: boolean; raison: string | null }>({ charge: !actif, raison: null })
  useEffect(() => {
    if (!actif) { setEtat({ charge: true, raison: null }); return }
    let vivant = true
    void apiRead<{ actions?: CapaciteAccess[] }>('/api/v1/access/capabilities')
      .then((r) => {
        if (!vivant) return
        const info = r.status === 200 && Array.isArray(r.data.actions) ? r.data.actions.find((a) => a.action === 'ACCESS_DOOR_UNLOCK') : undefined
        setEtat({ charge: true, raison: info && info.status !== 'AVAILABLE' ? (info.reason ?? 'Action indisponible.') : null })
      })
      // Capacités illisibles : pas de blocage inventé, le serveur tranchera.
      .catch(() => { if (vivant) setEtat({ charge: true, raison: null }) })
    return () => { vivant = false }
  }, [actif])
  return etat
}

export function InterphoneAppelPage({ id }: { id: string }) {
  const { permissions } = usePermissions()
  const peutOuvrir = permissions.includes('access.doors.unlock')
  // Sans access.view, les capacités ne se lisent pas : repli sur la décision du serveur au clic.
  const blocage = useBlocageOuverture(peutOuvrir && permissions.includes('access.view'))
  const confirm = useConfirm()
  const toast = useToast()
  const [l, setL] = useState<Lecture>({ etat: 'chargement' })
  const [ouverture, setOuverture] = useState(false)

  useEffect(() => {
    let vivant = true
    void apiRead<FicheAppel>(`/api/v1/interphone/appels/${encodeURIComponent(id)}`)
      .then((r) => { if (vivant) setL(r.status === 200 ? { etat: 'ok', data: r.data } : { etat: 'erreur', message: messageErreur(r.status, r.data, 'Cet appel est indisponible.') }) })
      .catch(() => { if (vivant) setL({ etat: 'erreur', message: 'Gestion n’a pas répondu.' }) })
    return () => { vivant = false }
  }, [id])

  const ouvrir = async () => {
    if (l.etat !== 'ok' || ouverture) return
    const porte = l.data.porte_staff
    const a = l.data.appel
    const ancien = !appelRecent(a.debut)
    if (!(await confirm({
      title: `Ouvrir la ${porte.nom} ?`,
      message: ancien
        ? `Cet appel date de ${quandParis(a.debut)} : la personne est peut-être repartie. La serrure sera réellement actionnée immédiatement.`
        : 'La serrure sera réellement actionnée immédiatement : la personne à l’interphone pourra entrer.',
      details: [
        { label: 'Porte', value: porte.nom },
        { label: 'Appel', value: a.resume || libelleType(a.type) },
        { label: 'Date de l’appel', value: quandParis(a.debut) },
      ],
      variant: 'critical', confirmLabel: 'Ouvrir la porte',
    }))) return
    setOuverture(true)
    try {
      const r = await runCommand('ACCESS_DOOR_UNLOCK', { door_id: porte.door_id })
      if (!r.ok) toast('error', r.error ?? 'Ouverture refusée.')
      else toast('ok', `${porte.nom} déverrouillée par UniFi Access.`)
    } catch {
      toast('error', 'Issue inconnue : vérifiez Contrôle d’accès › Historique avant de recommencer.')
    } finally {
      setOuverture(false)
    }
  }

  if (l.etat !== 'ok') {
    return (
      <main className="lc-page lc-page--readable lc-stagger ip-page">
        <Trail leaf="Appel" />
        <SmartBack fallback="/securite/interphone-ia" />
        {l.etat === 'chargement' ? <p className="lc-hint" aria-busy="true">Chargement de l’appel…</p> : <p className="ip-gris" role="status">{l.message}</p>}
      </main>
    )
  }

  const a = l.data.appel
  const tours = Array.isArray(a.tours) ? a.tours : []
  const audio = new Map((Array.isArray(l.data.audio) ? l.data.audio : []).map((x) => [x.n, x]))
  const recent = appelRecent(a.debut)
  return (
    <main className="lc-page lc-page--readable lc-stagger ip-page">
      <Trail leaf={`Appel de ${quandParis(a.debut)}`} />
      <SmartBack fallback="/securite/interphone-ia" />
      <header className="ip-hero">
        <div className="ip-hero__texte">
          <h1 className="lc-title ip-fiche__titre">{a.resume || libelleType(a.type)}</h1>
          <p className="lc-subtitle">Appel de {quandParis(a.debut)} · {duree(a.duree_s)}</p>
        </div>
        {peutOuvrir ? (
          <div className="ip-hero__actions">
            {blocage.raison ? (
              <span className="ip-bloque">
                <button type="button" className="lc-btn" disabled aria-disabled="true" aria-describedby="ip-porte-raison">
                  <Lock size={14} aria-hidden="true" /> Ouvrir la porte STAFF
                </button>
                <span className="ip-bloque__raison" id="ip-porte-raison">{blocage.raison}</span>
              </span>
            ) : (
              <button type="button" className={`lc-btn${recent ? ' lc-btn--primary' : ''}`} disabled={ouverture || !blocage.charge}
                onClick={() => { void ouvrir() }}>
                <DoorOpen size={16} aria-hidden="true" />{' '}
                {ouverture ? 'Ouverture…' : recent ? 'Ouvrir la porte STAFF' : `Ouvrir maintenant (appel de ${quandParis(a.debut).includes(' ') ? quandParis(a.debut) : heureParis(a.debut)})`}
              </button>
            )}
          </div>
        ) : null}
      </header>

      <section className="lc-panel ip-section" aria-labelledby="ip-resume">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-resume">Résumé</h2></header>
        <div className="lc-panel__body">
          <dl className="ip-faits">
            <div><dt>Répondu par</dt><dd>{a.origine === 'secours'
              ? <span className="lc-pill lc-pill--warning"><PhoneForwarded size={12} aria-hidden="true" /> Téléphones de secours (Sacha n’a pas répondu)</span>
              : a.origine === 'repertoire' ? 'Message « Répertoire » (visiteur invité à choisir le motif sur l’interphone)' : 'Sacha'}</dd></div>
            <div><dt>Motif</dt><dd>{libelleType(a.type)}</dd></div>
            <div><dt>Suite donnée</dt><dd>{libelleAction(a.action)}{a.equipe ? ` · ${libelleEquipe(a.equipe)}` : ''}</dd></div>
            <div><dt>{libelleCibles(a.cibles)}</dt><dd>{a.cibles || '—'}</dd></div>
            <div><dt>Décroché</dt><dd>{libelleDecroche(a.decroche)}</dd></div>
            {a.suite ? <div><dt>Après la sonnerie</dt><dd>{a.suite.texte}</dd></div> : null}
            {a.club_ferme ? <div><dt>Moment</dt><dd><Moon size={13} aria-hidden="true" /> Club fermé</dd></div> : null}
          </dl>
        </div>
      </section>

      <section className="lc-panel ip-section" aria-labelledby="ip-conversation">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="ip-conversation">Conversation</h2>
          <span className="lc-hint">{tours.length} échange{tours.length > 1 ? 's' : ''}</span>
        </header>
        <div className="lc-panel__body">
          {tours.length === 0 ? <p className="lc-hint">Aucun échange enregistré pour cet appel.</p> : (
            <ol className="ip-tours">
              {tours.map((t, i) => <Echange key={i} n={i} t={t} id={a.id} audioDispo={audio.has(i)} />)}
            </ol>
          )}
        </div>
      </section>
    </main>
  )
}

type EtatAudio = { etat: 'repos' } | { etat: 'chargement' } | { etat: 'pret'; url: string } | { etat: 'erreur'; message: string }

function Echange({ n, t, id, audioDispo }: { n: number; t: Tour; id: string; audioDispo: boolean }) {
  const [audio, setAudio] = useState<EtatAudio>({ etat: 'repos' })
  const urlRef = useRef<string | null>(null)
  // L'URL du blob est libérée en quittant la fiche.
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current) }, [])

  const charger = async () => {
    setAudio({ etat: 'chargement' })
    try {
      // Lecture avec le statut : un 404 (effacé) n'est pas un 503 (service muet) ni un 403.
      const r = await fetch(cheminAudio(id, n), { credentials: 'same-origin' })
      if (!r.ok) {
        const data: unknown = await r.json().catch(() => ({}))
        setAudio({ etat: 'erreur', message: messageAudio(r.status, data) })
        return
      }
      const url = URL.createObjectURL(await r.blob())
      urlRef.current = url
      setAudio({ etat: 'pret', url })
    } catch {
      setAudio({ etat: 'erreur', message: messageAudio(0, null) })
    }
  }

  const delais = [
    ['compris', delai(t.compris_s)], ['réfléchi', delai(t.reflechi_s)], ['premier son', delai(t.premier_son_s)],
  ].filter((x): x is [string, string] => x[1] !== null)
  return (
    <li className="ip-tour">
      <span className="ip-tour__num">Échange {n + 1}</span>
      <p className="ip-tour__ligne"><Ear size={14} aria-hidden="true" /> <span><strong>Entendu :</strong> {t.entendu?.trim() || '(rien de compris)'}</span></p>
      <p className="ip-tour__ligne"><MessageSquare size={14} aria-hidden="true" /> <span><strong>Sacha :</strong> {t.dire?.trim() || '(silence)'}</span></p>
      {delais.length > 0 ? <p className="ip-tour__delais">{delais.map(([k, v]) => `${k} ${v}`).join(' · ')}</p> : null}
      {!audioDispo ? (
        <p className="lc-hint">Enregistrement indisponible : la voix est effacée après 30 jours (le texte reste 6 mois).</p>
      ) : audio.etat === 'pret' ? (
        <audio className="ip-audio" controls autoPlay src={audio.url} aria-label={`Voix du visiteur, échange ${n + 1}`} />
      ) : (
        <div className="ip-ecoute">
          <button type="button" className="lc-btn lc-btn--ghost" disabled={audio.etat === 'chargement'} onClick={() => { void charger() }}>
            <Play size={14} aria-hidden="true" /> {audio.etat === 'chargement' ? 'Chargement…' : 'Écouter la voix du visiteur'}
          </button>
          {audio.etat === 'erreur' ? <p className="ip-gris" role="status">{audio.message}</p> : null}
        </div>
      )}
    </li>
  )
}
